import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CalculateIcon from "@mui/icons-material/Calculate";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../generated/api-client";
import toast from "react-hot-toast";
import { ToolModal, ToolDefinition } from "../components/ToolModal";
import PoweredByFooter from "../components/PoweredByFooter";

const ALL_TOOLS: ToolDefinition[] = [
  {
    id: "cost_estimation",
    name: "Stima Costi",
    description: "Importa una BOM, abbina ogni riga al catalogo e genera un preventivo di costo",
    icon: <CalculateIcon />,
    color: "#1565C0",
    kind: "route",
    path: "/cost-estimation",
  },
  {
    id: "pdf_converter",
    name: "Convertitore PDF File di Gara",
    description: "Carica un PDF di computo metrico e scarica l'elenco voci in formato Excel",
    icon: <PictureAsPdfIcon />,
    color: "#C62828",
    kind: "modal",
  },
  {
    id: "analytics_coming_soon",
    name: "Analytics",
    description: "Dashboard di analisi e reportistica avanzata",
    icon: <AutoGraphIcon />,
    color: "#7B1FA2",
    kind: "coming_soon",
  },
];

const TOOL_GROUPS = [
  {
    groupName: "Strumenti",
    toolIds: ["cost_estimation", "pdf_converter", "analytics_coming_soon"],
  },
];

const HomePage: React.FC = () => {
  const { user, hasTool, inviteUser } = useAuth();
  const navigate = useNavigate();

  const [activeTool, setActiveTool] = useState<ToolDefinition | null>(null);
  const [toolModalOpen, setToolModalOpen] = useState(false);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.BaseUser);
  const [inviteLoading, setInviteLoading] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "mantainer";

  const handleOpenTool = (tool: ToolDefinition) => {
    if (tool.kind === "route" && tool.path) {
      navigate(tool.path);
      return;
    }
    setActiveTool(tool);
    setToolModalOpen(true);
  };

  const handleCloseToolModal = () => {
    setToolModalOpen(false);
    setTimeout(() => setActiveTool(null), 300);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) { toast.error("Inserisci un'email"); return; }
    setInviteLoading(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteRole);
      toast.success(`Invito inviato a ${inviteEmail}`);
      setInviteDialogOpen(false);
      setInviteEmail("");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio dell'invito");
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100%" }}>
    <Box sx={{ flexGrow: 1, p: { xs: 3, md: 5 }, maxWidth: 1200, mx: "auto", width: "100%" }}>
      {/* Heading */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
          Strumenti
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Seleziona uno strumento per iniziare
        </Typography>
      </Box>

      {/* Tool groups */}
      {TOOL_GROUPS.map((group) => {
        const visibleTools = ALL_TOOLS.filter(
          (t) => group.toolIds.includes(t.id) && (t.kind === "coming_soon" || hasTool(t.id))
        );
        if (visibleTools.length === 0) return null;
        return (
          <Box key={group.groupName} sx={{ mb: 5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
                {group.groupName}
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
              {visibleTools.map((tool) => (
                <Box
                  key={tool.id}
                  onClick={() => handleOpenTool(tool)}
                  sx={{
                    width: { xs: "100%", sm: 210 },
                    p: 2.5,
                    borderRadius: "16px",
                    border: "1.5px solid",
                    borderColor: "rgba(0,0,0,0.08)",
                    cursor: "pointer",
                    bgcolor: "background.paper",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    userSelect: "none",
                    "&:hover": {
                      borderColor: tool.color + "80",
                      boxShadow: `0 6px 24px ${tool.color}18`,
                      transform: "translateY(-3px)",
                    },
                    "&:active": { transform: "translateY(-1px)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      bgcolor: tool.color + "16",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: tool.color,
                      "& svg": { fontSize: "1.6rem" },
                    }}
                  >
                    {tool.icon}
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}>
                        {tool.name}
                      </Typography>
                      {tool.kind === "coming_soon" && (
                        <Chip label="Soon" size="small" sx={{ height: 16, fontSize: "0.65rem", bgcolor: tool.color + "14", color: tool.color }} />
                      )}
                      {tool.kind === "route" && (
                        <Chip label="Test" size="small" sx={{ height: 16, fontSize: "0.65rem", bgcolor: "#1565C014", color: "#1565C0" }} />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.4, display: "block" }}>
                      {tool.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}

      {/* Admin section */}
      {isAdmin && (
        <Box sx={{ mt: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
              Amministrazione
            </Typography>
            <Chip label="Admin" size="small" sx={{ bgcolor: "#00A19C18", color: "#00897B", fontWeight: 600 }} />
            <Divider sx={{ flexGrow: 1 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              component={Link}
              to="/users"
              sx={{ borderRadius: "10px", px: 2.5, py: 1.2 }}
            >
              Gestisci Utenti
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteDialogOpen(true)}
              sx={{ borderRadius: "10px", px: 2.5, py: 1.2 }}
            >
              Invita Utente
            </Button>
          </Box>
        </Box>
      )}

      {/* Tool modal (legacy/coming-soon) */}
      <ToolModal open={toolModalOpen} onClose={handleCloseToolModal} tool={activeTool} />

      {/* Invite dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Invita Nuovo Utente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label="Ruolo"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              fullWidth
              required
            >
              <MenuItem value={UserRole.BaseUser}>Utente Base</MenuItem>
              <MenuItem value={UserRole.Mantainer}>Mantainer</MenuItem>
              <MenuItem value={UserRole.Admin}>Amministratore</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviteLoading}>Annulla</Button>
          <Button onClick={handleInviteUser} variant="contained" disabled={inviteLoading || !inviteEmail.trim()}>
            {inviteLoading ? <CircularProgress size={24} /> : "Invia Invito"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    <PoweredByFooter variant="dark" />
    </Box>
  );
};

export default HomePage;
